import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authAPI, webauthnAPI } from '@/lib/api';
import { signInWithGoogle, signInWithApple } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff, Fingerprint } from 'lucide-react';

// Helper to convert base64url to Uint8Array
function base64urlToUint8Array(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}

// Helper to convert Uint8Array to base64url
function uint8ArrayToBase64url(array) {
  const binary = String.fromCharCode.apply(null, array);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Helper to convert ArrayBuffer to base64url
function arrayBufferToBase64url(buffer) {
  return uint8ArrayToBase64url(new Uint8Array(buffer));
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricRegistered, setBiometricRegistered] = useState(false);

  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(redirectUrl, { replace: true });
    }
  }, [user, navigate, redirectUrl]);

  useEffect(() => {
    checkBiometricAvailability();
    // Load remembered email
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setLoginForm(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
      // Check if biometric is available for this email
      checkBiometricForEmail(rememberedEmail);
    }
  }, []);

  const checkBiometricAvailability = async () => {
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } catch (e) {
        setBiometricAvailable(false);
      }
    }
  };

  const checkBiometricForEmail = async (email) => {
    if (!email) return;
    try {
      const res = await webauthnAPI.checkAvailable(email);
      setBiometricRegistered(res.data.available);
    } catch (e) {
      setBiometricRegistered(false);
    }
  };

  // Check when email changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loginForm.email && loginForm.email.includes('@')) {
        checkBiometricForEmail(loginForm.email);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [loginForm.email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.login({ ...loginForm, remember_me: rememberMe });
      
      // Save or remove remembered email
      if (rememberMe) {
        localStorage.setItem('remembered_email', loginForm.email);
      } else {
        localStorage.removeItem('remembered_email');
      }
      
      if (res.data.requires_otp) {
        toast.success('Codice OTP inviato alla tua email');
        navigate('/verify-otp', {
          state: { userId: res.data.user_id, otpCode: res.data.otp_code, rememberMe }
        });
      } else if (res.data.token && res.data.user) {
        // Login diretto senza OTP (2FA disabilitato o dispositivo attendibile)
        localStorage.setItem('auth_token', res.data.token);
        login(res.data.user, res.data.token);
        toast.success('Accesso effettuato!');
        // Force page reload to ensure auth state is properly initialized
        window.location.href = redirectUrl;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore di login');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!loginForm.email) {
      toast.error('Inserisci prima la tua email');
      return;
    }

    setLoading(true);
    try {
      // Get authentication options from server
      const optionsRes = await webauthnAPI.getAuthenticationOptions(loginForm.email);
      const options = JSON.parse(optionsRes.data.options);

      // Prepare credential request options
      const publicKeyCredentialRequestOptions = {
        challenge: base64urlToUint8Array(options.challenge),
        timeout: options.timeout || 60000,
        rpId: optionsRes.data.rp_id,
        userVerification: options.userVerification || 'required',
        allowCredentials: (options.allowCredentials || []).map(cred => ({
          id: base64urlToUint8Array(cred.id),
          type: cred.type,
          transports: cred.transports
        }))
      };

      // Request credential from authenticator
      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      if (credential) {
        // Prepare credential for verification
        const credentialJSON = {
          id: credential.id,
          rawId: arrayBufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
            authenticatorData: arrayBufferToBase64url(credential.response.authenticatorData),
            signature: arrayBufferToBase64url(credential.response.signature),
            userHandle: credential.response.userHandle ? arrayBufferToBase64url(credential.response.userHandle) : null
          },
          authenticatorAttachment: credential.authenticatorAttachment
        };

        // Verify with server
        const verifyRes = await webauthnAPI.verifyAuthentication(loginForm.email, credentialJSON);
        
        // Login successful - save token and user
        localStorage.setItem('auth_token', verifyRes.data.token);
        localStorage.setItem('user', JSON.stringify(verifyRes.data.user));
        
        toast.success('Accesso biometrico completato!');
        navigate(redirectUrl);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Autenticazione biometrica annullata');
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Errore nell\'autenticazione biometrica');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.register(registerForm);
      toast.success('Registrazione completata! Verifica il codice OTP');
      navigate('/verify-otp', {
        state: { userId: res.data.user_id, otpCode: res.data.otp_code }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore di registrazione');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      
      if (result.success) {
        // Send Firebase token to backend for verification and login
        const response = await authAPI.googleLogin({
          id_token: result.idToken,
          access_token: result.accessToken,
          email: result.email,
          name: result.name,
        });
        
        const { token, user: userData } = response.data;
        
        if (token && userData) {
          login(userData, token);
          toast.success('Login con Google effettuato!');
          navigate(redirectUrl, { replace: true });
        }
      } else {
        if (result.code === 'auth/popup-closed-by-user') {
          // User closed popup, don't show error - loading will be reset in finally
        } else {
          toast.error(result.error || 'Errore durante il login con Google');
        }
      }
    } catch (err) {
      console.error('Google login error:', err);
      toast.error(err.response?.data?.detail || 'Errore durante il login con Google');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setLoading(true);
    try {
      const result = await signInWithApple();
      
      if (result.success) {
        console.log('Sending to backend:', {
          firebase_id_token: result.firebaseIdToken ? 'present' : 'missing',
          user_identifier: result.appleUserId,
          email: result.email,
        });
        
        // Send Firebase token to backend for verification and login
        const response = await authAPI.appleLogin({
          firebase_id_token: result.firebaseIdToken,
          identity_token: result.idToken,
          user_identifier: result.appleUserId,
          email: result.email,
          full_name: result.name,
        });
        
        const { token, user: userData } = response.data;
        
        if (token && userData) {
          login(userData, token);
          toast.success('Login con Apple effettuato!');
          navigate(redirectUrl, { replace: true });
        }
      } else {
        if (result.code === 'auth/popup-closed-by-user') {
          // User closed popup, don't show error - loading will be reset in finally
        } else {
          toast.error(result.error || 'Errore durante il login con Apple');
        }
      }
    } catch (err) {
      console.error('Apple login error:', err);
      toast.error(err.response?.data?.detail || 'Errore durante il login con Apple');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1760978632114-0939f0d60045?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZWNobm9sb2d5JTIwbWluaW1hbGlzdCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcyMzc0NjUyfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center bg-white/10 backdrop-blur shadow-lg">
              <img src="/obc-logo.png" alt="OBC Cloud" className="w-16 h-16 object-contain" />
            </div>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            OBC Cloud
          </h1>
          <p className="text-lg text-white/70 max-w-md" style={{ fontFamily: 'IBM Plex Sans' }}>
            La piattaforma cloud per la gestione dei tuoi OBC
          </p>
        </div>
      </div>

      {/* Right side - auth forms */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-14 h-14 rounded-lg overflow-hidden flex items-center justify-center mr-3 shadow-md">
              <img src="/obc-logo.png" alt="OBC Cloud" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>OBC Cloud</h1>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-secondary">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Benvenuto</CardTitle>
                  <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          data-testid="login-email"
                          type="email"
                          placeholder="nome@email.com"
                          className="pl-10"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          data-testid="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="La tua password"
                          className="pl-10 pr-10"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="remember-me" 
                          checked={rememberMe}
                          onCheckedChange={setRememberMe}
                          data-testid="remember-me"
                        />
                        <Label 
                          htmlFor="remember-me" 
                          className="text-sm cursor-pointer text-muted-foreground"
                        >
                          Ricordami
                        </Label>
                      </div>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Password dimenticata?
                      </Link>
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="login-submit"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? 'Accesso...' : 'Accedi'}
                    </Button>
                  </form>

                  {biometricAvailable && biometricRegistered && (
                    <>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">accesso rapido</span>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        data-testid="biometric-login-btn"
                        className="w-full border-border hover:bg-accent"
                        onClick={handleBiometricLogin}
                        disabled={loading}
                      >
                        <Fingerprint className="w-5 h-5 mr-2" />
                        Accedi con Impronta Digitale
                      </Button>
                    </>
                  )}

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">oppure</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    data-testid="google-login-btn"
                    className="w-full border-border hover:bg-accent"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Accedi con Google
                  </Button>

                  <Button
                    variant="outline"
                    data-testid="apple-login-btn"
                    className="w-full border-border hover:bg-accent mt-2"
                    onClick={handleAppleLogin}
                    disabled={loading}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Accedi con Apple
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-border bg-card">
                <CardHeader>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Crea Account</CardTitle>
                  <CardDescription>14 giorni di prova gratuita inclusi</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Nome</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="reg-name"
                          data-testid="register-name"
                          placeholder="Il tuo nome"
                          className="pl-10"
                          value={registerForm.name}
                          onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="reg-email"
                          data-testid="register-email"
                          type="email"
                          placeholder="nome@email.com"
                          className="pl-10"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="reg-password"
                          data-testid="register-password"
                          type="password"
                          placeholder="Minimo 6 caratteri"
                          className="pl-10"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="register-submit"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      disabled={loading}
                    >
                      {loading ? 'Registrazione...' : 'Registrati'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
